import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { formGroup, formControl, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { TabsBlockSettings } from './TabsBlockSettings';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { Container } from 'reactstrap';
import { FormText } from '@core/components/form';
import { useDidMount } from '@core/utils/hooks/react';
import { useBloc } from '@core/utils/bloc';
import { TabsBlockBloc } from './TabsBlockBloc';
import './index.scss';
import { SortableContainer, SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';

export const TABS_BLOCK_AI_INSTRUCTION = `
TABS BLOCK - AI Instruction Format for Widget Variation

The "tabs" block supports tabbed content display. Each tab contains a title and a paragraph. The user can switch between tabs to read content. Ideal for presenting grouped information such as features, topics, comparisons, etc.

Supported Variation:
- "tabs"

Expected JSON Structure:
- type: "tabs"
- variation: "tabs"
- data: {
    tabs: [
      {
        tab: string,          // Tab title
        paragraph: string     // Tab content
      }
    ],
  }

Example JSON:
{
  "type": "tabs",
  "variation": "tabs",
  "data": {
    "tabs": [
      {
        "tab": "Overview",
        "paragraph": "This section provides a summary of the entire module."
      },
      {
        "tab": "Details",
        "paragraph": "This section covers detailed points and key concepts."
      }
    ],
  }
}
`;

export enum TabsBlockVariation {
  tabs = 'tabs',
}

function defineTabsForm() {
  return formGroup({
    tab: formControl<string>({
      initialValue: 'Tab',
      label: 'Title',
    }),
    paragraph: formControl<string>({
      label: 'Description',
      initialValue:
        'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
    }),
  });
}

function defineTabsBlockForm() {
  return formGroup({
    tabs: formArray(() => defineTabsForm()),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type TabsForm = ReturnType<typeof defineTabsForm>;
export type TabsBlockForm = ReturnType<typeof defineTabsBlockForm>;

export const TabsBlock = memo((props: CourseBuilder.BlockProps<TabsBlockVariation>) => {
  const { variation, preview, lessonId, editable } = props;
  const form = useMemo(defineTabsBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(TabsBlockBloc, lessonId);
  const tabs = useObservable(form.controls.tabs.items$, []);
  const [activeIndex, setActiveIndex] = useState(0);
  const isEditable = props.editable;
  const [tabItems, setTabItems] = useState<any[]>([]);

  useEffect(() => {
    if (!props.preview && tabs.length === 0) {
      form.controls.tabs.push();
    }
  }, [form, props.preview, tabs]);

  useEffect(() => {
    setTabItems(tabs);
  }, [tabs, tabItems]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newTabItems = R.move(tabItems, oldIndex, newIndex);
      setTabItems(newTabItems);
    },
    [tabItems, setTabItems],
  );

  useConfigureBlock({
    props,
    form,
    contentComponent: TabsBlockSettings,
    contentProps: { form },
  });

  const nextTab = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  useDidMount(() => {
    if (props.preview && !formValue.tabs.length) {
      form.patchValue({
        tabs: [
          {
            tab: 'Tab',
            paragraph:
              'Quam nisi convallis imperdiet porta venenatis ultricies litora netus in pellentesque vulputate laoreet, integer varius metus velit conubia leo purus egestas curabitur cubilia.',
          },
        ],
      });
    }
  });

  const removeTab = useCallback(
    (index: number) => {
      form.controls.tabs.removeAt(index);
    },
    [form],
  );

  const addTab = useCallback(() => {
    form.controls.tabs.push();
  }, [form]);

  return (
    <BlockContainer className={`block-container d-flex flex-fill ${variation}`} advanceForm={formValue.advance}>
      <Container>
        <ul className="nav nav-tabs mb-3" id="ex1" role="tablist">
          <SortableList
            axis={'xy'}
            items={tabItems}
            onSortEnd={onSortEnd}
            form={form}
            key={form.uid}
            preview={!!props.preview}
            activeIndex={activeIndex}
            nextTab={nextTab}
            isEditable={!!props.editable}
            removeTab={removeTab}
            addTab={addTab}
            useDragHandle={true}
          />
        </ul>

        <div className="tab-content" id="ex1-content">
          {tabs.map((controls, index) => {
            return <TabBodyItem form={controls} index={index} activeIndex={activeIndex} isEditable={!!props.editable} />;
          })}
        </div>
      </Container>
    </BlockContainer>
  );
});

interface TabBodyItemProps {
  form: TabsForm;
  index: number;
  activeIndex: number;
  isEditable: boolean;
}

const TabBodyItem = memo((props: TabBodyItemProps) => {
  const { form, index, activeIndex, isEditable } = props;

  return (
    <div
      className={`tab-pane fade ${index == activeIndex ? 'show active' : ''}`}
      id={`${index}-tab-body`}
      role="tabpanel"
      aria-labelledby={`${index}-tab`}
    >
      <FormText control={form.controls.paragraph} editable={isEditable} />
    </div>
  );
});

interface SortableListProps {
  items: TabsForm[];
  form: TabsBlockForm;
  preview: boolean;
  key: string;
  activeIndex: number;
  nextTab: (val: number) => void;
  isEditable: boolean;
  removeTab: (val: number) => void;
  addTab: () => void;
}

const SortableList = SortableContainer((props: SortableListProps) => {
  const { items, form, preview, key, activeIndex, nextTab, isEditable, addTab, removeTab } = props;

  return (
    <div className="parent-div">
      {items.map((item, index) => {
        return (
          <SortableListItem
            key={key}
            index={index}
            item={item}
            form={form}
            preview={preview}
            activeIndex={activeIndex}
            nextTab={nextTab}
            length={items.length}
            isEditable={isEditable}
            removeTab={removeTab}
            addTab={addTab}
            i={index}
          />
        );
      })}
    </div>
  );
});

interface SortableListItemProps {
  index: number;
  i: number;
  item: TabsForm;
  form: TabsBlockForm;
  preview: boolean;
  key: string;
  activeIndex: number;
  nextTab: (val: number) => void;
  length: number;
  isEditable: boolean;
  removeTab: (val: number) => void;
  addTab: () => void;
}

const SortableListItem = SortableElement((props: SortableListItemProps) => {
  const { i, item, activeIndex, nextTab, length, isEditable, addTab, removeTab } = props;
  const DragHandle = SortableHandle(() => <i className="bx bx-move font-size-16"></i>);

  return (
    <>
      <li className="nav-item" role="presentation">
        {i == activeIndex ? (
          <div className="nav-link show active" id={`${i}-tab`} data-mdb-toggle="tab" role="tab" aria-controls="ex1-tabs-1" aria-selected={true}>
            <FormText control={item.controls.tab} editable={isEditable} />
            {isEditable && (
              <div>
                {i != 0 && (
                  <a className="text-danger ml-4" onClick={() => removeTab(i)}>
                    <i className="bx bx-x"></i>
                  </a>
                )}
                <a className="ml-2">
                  <DragHandle />
                </a>
              </div>
            )}
          </div>
        ) : (
          <a
            className="nav-link"
            id={`${i}-tab`}
            data-mdb-toggle="tab"
            href={`#${i}-tab-body`}
            role="tab"
            aria-controls="ex1-tabs-1"
            aria-selected={false}
            onClick={() => {
              nextTab(i);
            }}
          >
            {item.controls.tab.value}
            {isEditable && (
              <div>
                {i != 0 && (
                  <a className="text-danger ml-4" onClick={() => removeTab(i)}>
                    <i className="bx bx-x"></i>
                  </a>
                )}
                <a className="ml-2">
                  <DragHandle />
                </a>
              </div>
            )}
          </a>
        )}
      </li>
      {length - 1 == i && isEditable && (
        <li className="mt-2">
          <a className="btn btn-outline-primary btn-sm ml-4" onClick={() => addTab()}>
            <i className="bx bx-plus"></i>
          </a>
        </li>
      )}
    </>
  );
});
